// ABOUTME: The Goal Grid header's behaviour that Horizon's header-menu and header-drawer elements lack, layered on them unchanged: the phone
// ABOUTME: drawer's Category | Goal tabs, arrow keys across the desktop menu bar and into its panels, and nav images loaded once reached for.
// Loaded by blocks/jf-goal-grid-menu.liquid and snippets/jf-goal-grid-drawer.liquid. The panels still open, close and take Escape in
// assets/header-menu.js; the arrow keys open and close a panel by pressing its disclosure button, so that file's state stays the only state.

const MENU = 'header-menu.jf-gg';
const ITEM = '.menu-list__list-item';
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The drawer's tabs, as the WAI-ARIA tabs pattern describes: clicking a tab, or moving to it with the left and right arrows, Home or
 * End, selects it and shows its panel; only the selected tab is in the Tab order.
 */
class JfGoalGridTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    return [...this.querySelectorAll(':scope > [role="tablist"] > [role="tab"]')].filter((tab) => tab instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} focus
   */
  #select(tab, focus) {
    for (const other of this.#tabs()) {
      const on = other === tab;
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(other.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !on;
    }
    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement && this.#tabs().includes(tab)) this.#select(tab, false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs();
    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current === -1) return;
    const moves = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: tabs.length - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const next = (moves[/** @type {keyof typeof moves} */ (event.key)] + tabs.length) % tabs.length;
    this.#select(tabs[next], true);
  };
}

if (!customElements.get('jf-gg-tabs')) {
  customElements.define('jf-gg-tabs', JfGoalGridTabs);
}

/**
 * @param {Element} element
 * @returns {boolean} Whether the element has a box (a closed panel's controls keep theirs; a hidden overflow item has none)
 */
function hasBox(element) {
  return element.getClientRects().length > 0;
}

/**
 * The menu bar's controls in order: each top-level item's link, then its disclosure button when it opens a panel, then the More
 * button while items overflow.
 * @param {Element} menu
 * @returns {HTMLElement[]}
 */
function barControls(menu) {
  // The overflow list gives the items in the bar an empty slot and the others 'overflow' or 'more'.
  const inBar = [...menu.querySelectorAll(ITEM)].filter((item) => item instanceof HTMLElement && item.slot === '');
  const controls = inBar.flatMap((item) => [
    ...item.querySelectorAll(':scope > [ref="menuitem"], :scope > .menu-list__trigger > [ref="menuitem"], :scope > .menu-list__trigger > [ref="disclosure[]"]'),
  ]);
  controls.push(...menu.querySelectorAll(`${ITEM}[slot="more"] [ref="menuitem"]`));
  return controls.filter((control) => control instanceof HTMLElement && hasBox(control));
}

/**
 * @param {Element} panel
 * @returns {HTMLElement[]} The panel's links and buttons in document order
 */
function panelControls(panel) {
  return [...panel.querySelectorAll(FOCUSABLE)].filter((control) => control instanceof HTMLElement && hasBox(control));
}

/**
 * Opens or closes a panel the way a shopper does, by pressing its disclosure button.
 * @param {Element | null} disclosure
 * @param {boolean} open
 */
function setOpen(disclosure, open) {
  if (disclosure instanceof HTMLElement && (disclosure.getAttribute('aria-expanded') === 'true') !== open) disclosure.click();
}

/**
 * Arrow keys in the desktop menu: left and right move across the bar, Home and End to its ends, down opens an item's
 * panel and moves into it; in a panel, down and up move through its links (up from the first returns to the disclosure button),
 * Home and End to its ends, and left or right closes it and moves to the next control in the bar. Escape stays with header-menu.js.
 * @param {KeyboardEvent} event
 */
function onMenuKeydown(event) {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const menu = target.closest(MENU);
  if (!menu || menu.closest('header-component')?.getAttribute('data-menu-style') !== 'menu') return;
  const item = target.closest(ITEM);
  if (!item || item.getAttribute('slot') === 'overflow') return;

  const controls = barControls(menu);
  const disclosure = item.querySelector(':scope > .menu-list__trigger > [ref="disclosure[]"]');
  const panel = item.querySelector(':scope > [ref="submenu[]"]');
  /** @type {HTMLElement | null | undefined} */
  let next;

  if (panel?.contains(target)) {
    const links = panelControls(panel);
    const at = links.indexOf(target);
    if (event.key === 'ArrowDown') next = links[Math.min(at + 1, links.length - 1)];
    else if (event.key === 'ArrowUp') next = at <= 0 && disclosure instanceof HTMLElement ? disclosure : links[at - 1];
    else if (event.key === 'Home') next = links[0];
    else if (event.key === 'End') next = links[links.length - 1];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const from = disclosure instanceof HTMLElement ? controls.indexOf(disclosure) : -1;
      setOpen(disclosure, false);
      next = controls[(from + (event.key === 'ArrowRight' ? 1 : -1) + controls.length) % controls.length];
    } else return;
  } else {
    const at = controls.indexOf(target);
    if (at === -1) return;
    if (event.key === 'ArrowRight') next = controls[(at + 1) % controls.length];
    else if (event.key === 'ArrowLeft') next = controls[(at - 1 + controls.length) % controls.length];
    else if (event.key === 'Home') next = controls[0];
    else if (event.key === 'End') next = controls[controls.length - 1];
    else if (event.key === 'ArrowDown' && disclosure && panel) {
      setOpen(disclosure, true);
      next = panelControls(panel)[0];
    } else return;
  }

  event.preventDefault();
  next?.focus();
}

document.addEventListener('keydown', onMenuKeydown);

/**
 * Nav images load once the shopper reaches for their part of the menu: a desktop item (its panel's images) or the phone drawer's
 * button (the drawer's), on a pointer moving over it, a touch or focus. Until then each address waits in data-jf-src, so the page
 * itself loads none of them. A header re-render (the section's idle hydration, a cart update) keeps a set address, because the
 * images carry data-skip-node-update; one that brings a reached part's images back unset has them set again.
 */
const reached = new Set();
let watchingRenders = false;
let setScheduled = false;

/** @param {Element} scope */
function loadImages(scope) {
  for (const image of scope.querySelectorAll('img[data-jf-src]')) {
    if (!(image instanceof HTMLImageElement)) continue;
    image.src = image.dataset.jfSrc || '';
    image.removeAttribute('data-jf-src');
  }
}

function reloadReached() {
  setScheduled = false;
  for (const key of reached) {
    document.querySelectorAll(`[data-jf-gg-intent="${CSS.escape(key)}"]`).forEach(loadImages);
  }
}

/** @param {Element} scope */
function reach(scope) {
  const key = scope.getAttribute('data-jf-gg-intent');
  if (!key) return;
  reached.add(key);
  loadImages(scope);
  if (watchingRenders) return;
  watchingRenders = true;
  // Watched from the layout's header group, which a re-render of the header section keeps while it replaces what is inside it.
  // Only a change that brings back an image still waiting for its address is acted on; a cart count or an announcement is not.
  new MutationObserver((records) => {
    if (setScheduled) return;
    const bringsImages = records.some(
      (record) =>
        record.type === 'attributes' ||
        [...record.addedNodes].some((node) => node instanceof Element && (node.matches('img[data-jf-src]') || node.querySelector('img[data-jf-src]') !== null))
    );
    if (!bringsImages) return;
    setScheduled = true;
    requestAnimationFrame(reloadReached);
  }).observe(document.getElementById('header-group') ?? document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-jf-src'],
  });
}

/** @param {Event} event */
function onIntent(event) {
  const scope = event.target instanceof Element ? event.target.closest('[data-jf-gg-intent]') : null;
  if (scope) reach(scope);
}

for (const type of ['pointerover', 'focusin', 'touchstart']) {
  document.addEventListener(type, onIntent, { capture: true, passive: true });
}

// A shopper who reached for the menu before this module ran.
document.querySelectorAll('[data-jf-gg-intent]').forEach((scope) => {
  if (scope.matches(':hover') || scope.contains(document.activeElement)) reach(scope);
});
